<template>
  <div class="card ml-2">
    <div
      class="header border-bottom accordian-toggle"
      v-b-toggle.id&account
      @click="handleToggle"
    >
      <h5 class="text-primary font-weight-normal mb-0 mr-4">
        ID and Account Details
      </h5>
    </div>
    <b-collapse id="id&account" :visible="initiallyExpanded">
      <div class="body">
        <!-- Identification Documents Section -->
        <div class="mb-4">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <h6 class="text-secondary mb-0">Identification Documents</h6>
            <b-button variant="primary" size="sm" @click="openCreateModal">
              <i class="fa fa-plus mr-1"></i>
              Add New ID Document
            </b-button>
          </div>

          <!-- Loading State -->
          <div v-if="!identificationsLoaded" class="identifications-loading">
            <div class="loading-card" v-for="n in 2" :key="n">
              <div class="loading-header">
                <div class="loading-badge"></div>
                <div class="loading-badge-sm"></div>
              </div>
              <div class="loading-body">
                <div class="loading-line"></div>
                <div class="loading-line short"></div>
                <div class="loading-line"></div>
              </div>
            </div>
          </div>

          <!-- Identifications Cards -->
          <div
            v-else-if="identifications.length > 0"
            class="identification-cards"
          >
            <div
              v-for="identification in identifications"
              :key="identification.id"
              class="identification-card"
              :class="{
                'is-expired': identification.isExpired,
                'is-active': identification.isActive,
              }"
            >
              <!-- Card Header -->
              <div class="id-card-header">
                <div class="badges d-flex align-items-center">
                  <span v-if="identification.isActive" class="active-badge">
                    <i class="fa fa-check-circle mr-1"></i>
                    Active
                  </span>
                  <span class="id-type-badge">
                    {{ identification.idTypeName || "Unknown" }}
                  </span>
                  <span
                    class="status-badge"
                    :class="
                      getHighLevelResultClass(
                        identification.parsedIdDetails?.highLevelResult,
                      )
                    "
                  >
                    {{
                      identification.parsedIdDetails?.highLevelResult || "N/A"
                    }}
                  </span>
                  <span
                    v-if="identification.isExpired"
                    class="ml-2 expired-badge"
                  >
                    <i class="fa fa-exclamation-triangle mr-1"></i>
                    EXPIRED
                  </span>
                </div>
                <div class="id-card-actions">
                  <b-button
                    variant="outline-primary"
                    size="sm"
                    @click="openEditModal(identification)"
                  >
                    <i class="fa fa-edit mr-1"></i>
                    Edit
                  </b-button>
                </div>
              </div>

              <!-- Card Body -->
              <div class="id-card-body">
                <div class="row">
                  <!-- Left Column -->
                  <div class="col-lg-6">
                    <!-- ID Number -->
                    <div class="id-field">
                      <label>ID Number</label>
                      <span class="id-value font-weight-bold">
                        {{ identification.idNumber }}
                      </span>
                    </div>

                    <!-- ID Type & Issue Country -->
                    <div class="id-field">
                      <label>ID Type / Issue Country</label>
                      <span class="id-value">
                        {{ identification.idTypeName || "N/A" }} /
                        {{
                          identification.parsedIdDetails?.issuingCountryName ||
                          identification.idCountryName ||
                          "N/A"
                        }}
                      </span>
                    </div>

                    <!-- ID Expiry Date -->
                    <div class="id-field">
                      <label>ID Expiry Date</label>
                      <span
                        class="id-value"
                        :class="{
                          'text-danger font-weight-bold':
                            identification.isExpired,
                        }"
                      >
                        {{ formatDate(identification.idExpiryDate) }}
                        <i
                          v-if="identification.isExpired"
                          class="fa fa-exclamation-circle ml-1 text-danger"
                        ></i>
                      </span>
                    </div>

                    <!-- Journey ID -->
                    <div class="id-field">
                      <label>Journey ID</label>
                      <span class="id-value text-monospace small">
                        {{ identification.parsedIdDetails?.journeyId || "N/A" }}
                      </span>
                    </div>
                  </div>

                  <!-- Right Column -->
                  <div class="col-lg-6">
                    <!-- ID First Name with match indicator -->
                    <div class="id-field">
                      <label>ID First Name</label>
                      <span class="id-value d-flex align-items-center">
                        {{
                          identification.parsedIdDetails?.idFirstName || "N/A"
                        }}
                        <span
                          v-if="identification.firstNameMatched !== undefined"
                          class="match-indicator ml-2"
                          :class="
                            identification.firstNameMatched
                              ? 'matched'
                              : 'not-matched'
                          "
                        >
                          <i
                            :class="
                              identification.firstNameMatched
                                ? 'fa fa-check-circle'
                                : 'fa fa-times-circle'
                            "
                          ></i>
                          {{
                            identification.firstNameMatched
                              ? "Matched"
                              : "Not Matched"
                          }}
                        </span>
                      </span>
                      <small class="text-muted">
                        Sender: {{ identification.senderFirstName || "N/A" }}
                      </small>
                    </div>

                    <!-- ID Last Name with match indicator -->
                    <div class="id-field">
                      <label>ID Last Name</label>
                      <span class="id-value d-flex align-items-center">
                        {{
                          identification.parsedIdDetails?.idLastName || "N/A"
                        }}
                        <span
                          v-if="identification.lastNameMatched !== undefined"
                          class="match-indicator ml-2"
                          :class="
                            identification.lastNameMatched
                              ? 'matched'
                              : 'not-matched'
                          "
                        >
                          <i
                            :class="
                              identification.lastNameMatched
                                ? 'fa fa-check-circle'
                                : 'fa fa-times-circle'
                            "
                          ></i>
                          {{
                            identification.lastNameMatched
                              ? "Matched"
                              : "Not Matched"
                          }}
                        </span>
                      </span>
                      <small class="text-muted">
                        Sender: {{ identification.senderLastName || "N/A" }}
                      </small>
                    </div>

                    <!-- ID DOB with match indicator -->
                    <div class="id-field">
                      <label>ID Date of Birth</label>
                      <span class="id-value d-flex align-items-center">
                        {{ formatDate(identification.parsedIdDetails?.idDob) }}
                        <span
                          v-if="identification.dobMatched !== undefined"
                          class="match-indicator ml-2"
                          :class="
                            identification.dobMatched
                              ? 'matched'
                              : 'not-matched'
                          "
                        >
                          <i
                            :class="
                              identification.dobMatched
                                ? 'fa fa-check-circle'
                                : 'fa fa-times-circle'
                            "
                          ></i>
                          {{
                            identification.dobMatched
                              ? "Matched"
                              : "Not Matched"
                          }}
                        </span>
                      </span>
                      <small class="text-muted">
                        Sender: {{ formatDate(identification.senderDob) }}
                      </small>
                    </div>

                    <!-- Sex -->
                    <div class="id-field">
                      <label>Sex</label>
                      <span class="id-value">
                        {{ identification.parsedIdDetails?.idSex || "N/A" }}
                      </span>
                    </div>
                  </div>
                </div>

                <!-- ID Image Section -->
                <div
                  class="id-image-section mt-3"
                  v-if="identification.idImageUrl"
                >
                  <label class="d-block mb-2">ID Document Image</label>
                  <div class="id-image-wrapper">
                    <img
                      :src="identification.idImageUrl"
                      :alt="'ID Document - ' + identification.idNumber"
                      class="id-image"
                      @click="openImageModal(identification)"
                      @error="handleImageError($event)"
                    />
                    <div
                      class="id-image-overlay"
                      @click="openImageModal(identification)"
                    >
                      <i class="fa fa-search-plus"></i>
                      <span>Click to enlarge</span>
                    </div>
                  </div>
                </div>

                <!-- MRZ Section -->
                <div
                  class="mrz-section mt-3"
                  v-if="identification.parsedIdDetails?.mrzFull"
                >
                  <label class="d-block mb-2">MRZ Data</label>
                  <div class="mrz-content">
                    <pre class="mb-0">{{
                      identification.parsedIdDetails.mrzFull
                    }}</pre>
                  </div>
                </div>

                <!-- Audit Info -->
                <div class="audit-section mt-3">
                  <div class="row">
                    <div class="col-6">
                      <small class="text-muted">
                        <i class="fa fa-plus-circle mr-1"></i>
                        Created: {{ formatDateTime(identification.createdAt) }}
                      </small>
                    </div>
                    <div class="col-6 text-right">
                      <small class="text-muted">
                        <i class="fa fa-edit mr-1"></i>
                        Updated: {{ formatDateTime(identification.updatedAt) }}
                      </small>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div v-else-if="identificationsLoaded" class="alert alert-info">
            <i class="fa fa-info-circle mr-2"></i>
            No identification documents found. Click "Add New ID Document" to
            create one.
          </div>
        </div>

        <hr class="my-4" />

        <!-- ID Details Section -->
        <h6 class="text-secondary mb-3">ID Details Status</h6>
        <ValidationObserver v-slot="{ passes }" class="channel-right">
          <form @submit.prevent="passes(handleVerificationSubmit)">
            <div class="edit-form">
              <div class="row">
                <div class="col-lg-6 col-12">
                  <div class="form-group datepicker">
                    <label for="accountExpiry" class="control-label">
                      ID Expiry
                    </label>
                    <span class="float-label">
                      <b-input-group>
                        <div class="input-group-prepend">
                          <span
                            class="input-group-text"
                            @click="$refs.accountexpiry.showCalendar()"
                          >
                            <i class="icon-calendar"></i>
                          </span>
                        </div>
                        <datepicker
                          class="form-control"
                          ref="accountexpiry"
                          v-model="accountInfo.accountExpiry"
                          :bootstrap-styling="false"
                          placeholder=""
                          :typeable="true"
                        ></datepicker>
                      </b-input-group>
                    </span>
                  </div>
                </div>
                <div class="col-lg-6 col-12">
                  <div class="d-flex justify-content-between flex-wrap mb-3">
                    <label for="passcodeverified" class="control-label">
                      Is Passcode Verified: *
                    </label>
                    <div>
                      <label class="fancy-radio">
                        <input
                          name="passcodeverified"
                          :value="1"
                          v-model="accountInfo.passcodeVerified"
                          type="radio"
                        />
                        <span>
                          <i></i>
                          Yes
                        </span>
                      </label>
                      <label class="fancy-radio">
                        <input
                          name="passcodeverified"
                          :value="0"
                          v-model="accountInfo.passcodeVerified"
                          type="radio"
                          checked
                        />
                        <span>
                          <i></i>
                          No
                        </span>
                      </label>
                    </div>
                  </div>
                  <div class="d-flex justify-content-between flex-wrap mb-3">
                    <label for="scanAttempt" class="control-label">
                      Do you want to reset Silver IDscan Attempt? [Count:
                      {{ accountInfo.count }}]: *
                    </label>
                    <div>
                      <label class="fancy-radio">
                        <input
                          name="scanAttempt"
                          :value="true"
                          v-model="accountInfo.scanAttempt"
                          type="radio"
                        />
                        <span>
                          <i></i>
                          Yes
                        </span>
                      </label>
                      <label class="fancy-radio">
                        <input
                          name="scanAttempt"
                          :value="false"
                          v-model="accountInfo.scanAttempt"
                          type="radio"
                        />
                        <span>
                          <i></i>
                          No
                        </span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
              <div
                class="d-flex flex-column flex-md-row justify-content-center"
              >
                <b-button
                  variant="secondary btn-mw"
                  size="lg"
                  @click="handleCancel"
                >
                  Cancel
                </b-button>
                <b-button
                  type="submit"
                  variant="primary ml-md-1 mt-2 mt-md-0 btn-mw"
                  size="lg"
                >
                  Submit
                </b-button>
              </div>
            </div>
          </form>
        </ValidationObserver>
      </div>
    </b-collapse>

    <!-- Edit/Create Identification Modal -->
    <b-modal
      :id="identificationModalId"
      :title="
        isCreatingNew
          ? 'Add New Identification Document'
          : 'Edit Identification Document'
      "
      size="lg"
      @hidden="resetIdentificationForm"
      ok-title="Save"
      cancel-variant="secondary"
    >
      <template #modal-footer="{ ok, cancel }">
        <div class="w-100 d-flex justify-content-between align-items-center">
          <div class="text-muted">
            <small v-if="!isCreatingNew && identificationForm.createdAt">
              Created: {{ formatDateTime(identificationForm.createdAt) }}
              <span v-if="identificationForm.updatedAt" class="ml-2">
                | Updated: {{ formatDateTime(identificationForm.updatedAt) }}
              </span>
            </small>
          </div>
          <div>
            <b-button variant="secondary" @click="cancel()">Cancel</b-button>
            <b-button
              variant="primary"
              @click="handleIdentificationSubmit"
              class="ml-2"
            >
              Save
            </b-button>
          </div>
        </div>
      </template>
      <ValidationObserver v-slot="{ passes }" ref="idFormObserver">
        <form @submit.prevent="passes(handleIdentificationSubmit)">
          <div class="row">
            <div class="col-12">
              <div
                class="form-group form-select single-multiselect select-option-custom"
              >
                <span class="float-label">
                  <label for="modal-idtype">ID Type *</label>
                  <multiselect
                    placeholder=""
                    v-model="identificationForm.idType"
                    :show-labels="false"
                    label="idtypename"
                    track-by="idtypeid"
                    :options="idTypeOptions"
                  ></multiselect>
                </span>
              </div>
              <div class="form-group">
                <span class="float-label">
                  <input
                    type="text"
                    class="form-control"
                    id="modal-idnumber"
                    v-model="identificationForm.idNumber"
                    placeholder="ID Number"
                  />
                  <label for="modal-idnumber" class="control-label">
                    ID Number *
                  </label>
                  <div class="line"></div>
                </span>
              </div>
              <div class="form-group">
                <span class="float-label">
                  <input
                    type="text"
                    class="form-control"
                    id="modal-iddetails"
                    v-model="identificationForm.idDetails"
                    placeholder="ID Details"
                    readonly
                    disabled
                  />
                  <label for="modal-iddetails" class="control-label">
                    ID Details
                  </label>
                  <div class="line"></div>
                </span>
              </div>
              <div class="form-group datepicker">
                <label for="modal-idExpireDate" class="control-label">
                  ID Expiry Date
                </label>
                <span class="float-label">
                  <b-input-group>
                    <div class="input-group-prepend">
                      <span
                        class="input-group-text"
                        @click="$refs.modalidexpiry.showCalendar()"
                      >
                        <i class="icon-calendar"></i>
                      </span>
                    </div>
                    <datepicker
                      class="form-control"
                      ref="modalidexpiry"
                      v-model="identificationForm.idExpiry"
                      :bootstrap-styling="false"
                      placeholder=""
                      :typeable="true"
                    ></datepicker>
                  </b-input-group>
                </span>
              </div>
              <div class="form-group">
                <b-form-checkbox
                  v-model="identificationForm.setAsActive"
                  name="setAsActive"
                  :value="true"
                  :unchecked-value="false"
                >
                  <span class="text-muted">
                    Set as active identification
                    <small class="d-block text-secondary">
                      {{
                        isCreatingNew
                          ? "This will be used as the primary ID for verification"
                          : "Make this the primary ID for verification"
                      }}
                    </small>
                  </span>
                </b-form-checkbox>
              </div>
            </div>
          </div>
        </form>
      </ValidationObserver>
    </b-modal>

    <!-- Image Preview Modal -->
    <b-modal
      id="id-image-modal"
      :title="'ID Document - ' + (selectedImageIdentification?.idNumber || '')"
      size="xl"
      centered
      hide-footer
      body-class="p-0"
    >
      <div class="image-modal-content">
        <img
          v-if="selectedImageIdentification?.idImageUrl"
          :src="selectedImageIdentification.idImageUrl"
          :alt="'ID Document - ' + selectedImageIdentification.idNumber"
          class="modal-id-image"
        />
        <div class="image-modal-info">
          <div class="row">
            <div class="col-md-4">
              <small class="text-muted">ID Type</small>
              <p class="mb-1">
                {{ selectedImageIdentification?.idTypeName || "N/A" }}
              </p>
            </div>
            <div class="col-md-4">
              <small class="text-muted">ID Number</small>
              <p class="mb-1">
                {{ selectedImageIdentification?.idNumber || "N/A" }}
              </p>
            </div>
            <div class="col-md-4">
              <small class="text-muted">Issue Country</small>
              <p class="mb-1">
                {{
                  selectedImageIdentification?.parsedIdDetails
                    ?.issuingCountryName ||
                  selectedImageIdentification?.idCountryName ||
                  "N/A"
                }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
const { ValidationObserver } = require("vee-validate");
import "@/plugins/vee-validate";
import { ISLOADING } from "@/store/mutations.type";
import {
  ID_TYPE_LIST,
  GET_CUSTOMER_ACCOUNT_DETAILS,
  GET_CUSTOMER_IDENTIFICATIONS,
  CREATE_CUSTOMER_IDENTIFICATION,
  UPDATE_CUSTOMER_IDENTIFICATION,
  UPDATE_CUSTOMER_VERIFICATION,
  RESET_ID_SCAN_ATTEMPTS,
} from "@/store/actions.type.js";
import { myFunction } from "@/common/common-helper.js";

export default {
  name: "IdAndAccountDetails",
  components: {
    ValidationObserver,
    Multiselect: () => import("vue-multiselect"),
    Datepicker: () => import("vuejs-datepicker"),
  },
  props: {
    customerId: {
      type: String,
      required: true,
    },
    countryOfResidenceId: {
      type: [String, Number],
      default: null,
    },
    accountStatuses: {
      type: Array,
      default: () => [],
    },
    initiallyExpanded: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      identifications: [],
      identificationsLoaded: false,
      identificationFields: [
        { key: "idTypeName", label: "ID Type", sortable: true },
        { key: "idNumber", label: "ID Number", sortable: true },
        { key: "idExpiryDate", label: "Expiry Date", sortable: true },
        { key: "actions", label: "Actions" },
      ],
      identificationModalId: "identification-modal",
      isCreatingNew: false,
      identificationForm: {
        id: null,
        idType: null,
        idNumber: "",
        idDetails: "",
        idExpiry: null,
        setAsActive: true,
        createdAt: null,
        updatedAt: null,
      },

      accountInfo: {
        accountExpiry: null,
        remitType: "N/A",
        remitStatus: null,
        isVerified: null,
        passcodeVerified: false,
        accountStatus: null,
        scanAttempt: false,
        notifyCustomer: false,
        count: 0,
        loaded: false,
      },
      idTypeOptions: [],
      selectedImageIdentification: null,
    };
  },
  mounted() {
    // If initially expanded, load data immediately
    if (this.initiallyExpanded) {
      this.loadData();
    }
  },
  methods: {
    async handleToggle() {
      if (this.accountInfo.loaded && this.identificationsLoaded) {
        return;
      }
      await this.loadData();
    },

    async loadData() {
      this.$store.commit(ISLOADING, true);

      // Only fetch ID types if countryOfResidenceId is provided
      if (this.countryOfResidenceId) {
        await this.$store
          .dispatch(ID_TYPE_LIST, this.countryOfResidenceId)
          .then((response) => {
            if (response.status == 200) {
              this.idTypeOptions = response.data.details;
            }
          })
          .catch((error) => {
            console.error("Error fetching ID types:", error);
            this.idTypeOptions = [];
          });
      } else {
        console.warn(
          "Country of residence ID is not available, skipping ID type list fetch",
        );
        this.idTypeOptions = [];
      }

      await Promise.all([this.loadIdentifications(), this.loadAccountInfo()]);

      this.$store.commit(ISLOADING, false);
    },

    async loadIdentifications() {
      this.$store
        .dispatch(GET_CUSTOMER_IDENTIFICATIONS, this.customerId)
        .then((response) => {
          if (response.status === 200) {
            const identifications = response.data.details || [];

            this.identifications = identifications.map((i) => ({
              ...i,
              // Use idTypeName from API if available, otherwise lookup
              idTypeName:
                i.idTypeName ||
                this.idTypeOptions.find((opt) => opt.idtypeid === i.idTypeId)
                  ?.idtypename ||
                "Unknown",
              idDetail: i.idDetails,
            }));
          }
        })
        .finally(() => {
          this.identificationsLoaded = true;
        });
    },

    getHighLevelResultClass(result) {
      if (!result) return "status-unknown";
      const lower = result.toLowerCase();
      if (lower === "passed") return "status-passed";
      if (lower === "refer") return "status-refer";
      if (lower === "failed") return "status-failed";
      return "status-unknown";
    },

    getAttemptStatusClass(status) {
      if (!status) return "";
      const lower = status.toLowerCase();
      if (lower === "passed") return "attempt-passed";
      if (lower === "refer") return "attempt-refer";
      if (lower === "failed") return "attempt-failed";
      return "";
    },

    openImageModal(identification) {
      this.selectedImageIdentification = identification;
      this.$bvModal.show("id-image-modal");
    },

    handleImageError(event) {
      event.target.style.display = "none";
      event.target.parentElement.innerHTML =
        '<div class="image-error"><i class="fa fa-image"></i><span>Image not available</span></div>';
    },

    async loadAccountInfo() {
      this.$store
        .dispatch(GET_CUSTOMER_ACCOUNT_DETAILS, this.customerId)
        .then((response) => {
          if (response.status == 200) {
            const accountDetails = response.data.details;

            if (accountDetails) {
              this.accountInfo = {
                accountExpiry: accountDetails.accountExpiryDate,
                remitType: accountDetails.remitType
                  ? accountDetails.remitType
                  : "N/A",
                remitStatus: accountDetails.remitStatus,
                isVerified: accountDetails.isVerified,
                passcodeVerified: accountDetails.passcodeVerified,
                accountStatus:
                  this.accountStatuses.find(
                    (option) => option.value === accountDetails.accountStatus,
                  ) || "Active",
                scanAttempt: accountDetails.resetIdscanAttempt,
                notifyCustomer: accountDetails.notifyRemiter,
                count: accountDetails.count,
              };
            }
          }

          this.accountInfo.loaded = true;
        });
    },

    openEditModal(identification) {
      this.isCreatingNew = false;
      this.identificationForm = {
        id: identification.id,
        idType:
          this.idTypeOptions.find(
            (opt) => opt.idtypeid === identification.idTypeId,
          ) || null,
        idNumber: identification.idNumber,
        idDetails: identification.idDetails || "",
        idExpiry: identification.idExpiryDate,
        setAsActive: false,
        createdAt: identification.createdAt,
        updatedAt: identification.updatedAt,
      };
      this.$bvModal.show(this.identificationModalId);
    },

    openCreateModal() {
      this.isCreatingNew = true;
      this.identificationForm = {
        id: null,
        idType: null,
        idNumber: "",
        idDetails: "",
        idExpiry: null,
        setAsActive: true,
        createdAt: null,
        updatedAt: null,
      };
      this.$bvModal.show(this.identificationModalId);
    },

    resetIdentificationForm() {
      this.identificationForm = {
        id: null,
        idType: null,
        idNumber: "",
        idDetails: "",
        idExpiry: null,
        setAsActive: true,
        createdAt: null,
        updatedAt: null,
      };
      this.isCreatingNew = false;
    },

    handleIdentificationSubmit() {
      if (
        !this.identificationForm.idType ||
        !this.identificationForm.idNumber
      ) {
        myFunction.showInfo("Please fill in all required fields", "error");
        return;
      }

      const params = {
        customerId: this.customerId,
        idTypeId: this.identificationForm.idType.idtypeid,
        idNumber: this.identificationForm.idNumber,
        idDetail: this.identificationForm.idDetails,
        idExpiryDate: this.identificationForm.idExpiry,
        setAsActive: this.identificationForm.setAsActive,
      };

      this.$store.commit(ISLOADING, true);

      const action = this.isCreatingNew
        ? CREATE_CUSTOMER_IDENTIFICATION
        : UPDATE_CUSTOMER_IDENTIFICATION;

      if (!this.isCreatingNew && this.identificationForm.id) {
        params.identificationId = this.identificationForm.id;
      }

      this.$store
        .dispatch(action, params)
        .then((response) => {
          this.$store.commit(ISLOADING, false);
          if (response.status == 200) {
            myFunction.showInfo(
              this.isCreatingNew
                ? "Identification document created successfully"
                : "Identification document updated successfully",
              "success",
            );
            this.$bvModal.hide(this.identificationModalId);

            // Reload identifications
            this.identificationsLoaded = false;
            this.loadIdentifications();
          } else {
            myFunction.showInfo(
              response.data.message || "An error occurred",
              "error",
            );
          }
        })
        .catch(() => {
          this.$store.commit(ISLOADING, false);
          myFunction.showInfo("An error occurred", "error");
        })
        .finally(() => {
          this.$store.commit(ISLOADING, false);
        });
    },

    async handleVerificationSubmit() {
      const params = {
        customerId: this.customerId,
        accountExpiryDate: this.accountInfo.accountExpiry,
        remitType:
          this.accountInfo.remitType == "N/A"
            ? this.accountInfo.remitType
            : null,
        remitStatus: this.accountInfo.remitStatus,
        isVerified: Boolean(this.accountInfo.isVerified),
        passcodeVerified: Boolean(this.accountInfo.passcodeVerified),
        accountStatus: this.accountInfo.accountStatus.value,
        notifyRemiter: this.accountInfo.notifyCustomer,
      };

      this.$store.commit(ISLOADING, true);

      try {
        const response = await this.$store.dispatch(
          UPDATE_CUSTOMER_VERIFICATION,
          params,
        );

        if (response.status == 200) {
          if (this.accountInfo.scanAttempt === true) {
            const resetResponse = await this.$store.dispatch(
              RESET_ID_SCAN_ATTEMPTS,
              this.customerId,
            );

            if (resetResponse.status == 200) {
              myFunction.showInfo(
                "Account details updated and ID scan attempts reset successfully",
                "success",
              );
            } else {
              myFunction.showInfo(
                "Account details updated but failed to reset ID scan attempts",
                "warning",
              );
            }
          } else {
            myFunction.showInfo(
              "Account details updated successfully",
              "success",
            );
          }

          this.accountInfo.loaded = false;
          await this.loadAccountInfo();
        } else {
          myFunction.showInfo(response.data.message, "error");
        }
      } catch (error) {
        myFunction.showInfo("An error occurred", "error");
      } finally {
        this.$store.commit(ISLOADING, false);
      }
    },

    handleCancel() {
      this.$router.push({ path: "/customer/list" });
    },

    formatDate(date) {
      if (!date) return "N/A";
      const d = new Date(date);
      if (isNaN(d.getTime())) return "N/A";
      return d.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    },

    formatDateTime(date) {
      if (!date) return "N/A";
      const d = new Date(date);
      if (isNaN(d.getTime())) return "N/A";
      return d.toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
  },
};
</script>

<style scoped>
/* Loading Skeleton */
.identifications-loading {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.loading-card {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background: #fff;
  overflow: hidden;
}

.loading-header {
  display: flex;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: #f8f9fa;
  border-bottom: 1px solid #e0e0e0;
}

.loading-badge {
  width: 80px;
  height: 24px;
  background: linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
}

.loading-badge-sm {
  width: 60px;
  height: 24px;
  background: linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
}

.loading-body {
  padding: 1rem;
}

.loading-line {
  height: 16px;
  background: linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
  margin-bottom: 0.75rem;
}

.loading-line.short {
  width: 60%;
}

.loading-line:last-child {
  margin-bottom: 0;
}

@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

/* Identification Cards */
.identification-cards {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.identification-card {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background: #fff;
  overflow: hidden;
  transition: box-shadow 0.2s ease;
}

.identification-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.identification-card.is-active {
  border-color: #5cc9a7;
  border-left: 4px solid #5cc9a7;
  box-shadow: 0 2px 8px rgba(92, 201, 167, 0.15);
}

.identification-card.is-expired {
  border-color: #dc3545;
  border-width: 2px;
}

/* Card Header */
.id-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1rem;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-bottom: 1px solid #e0e0e0;

  .badges {
    gap: 0.25rem;
  }
}
.is-active .id-card-header {
  background: linear-gradient(135deg, #f0fdf7 0%, #e6f9f0 100%);
}

.is-expired .id-card-header {
  background: linear-gradient(135deg, #fff5f5 0%, #ffe3e3 100%);
}

.id-type-badge {
  background: #5cc9a7;
  color: #fff;
  padding: 0.25rem 0.75rem;
  border-radius: 4px;
  font-size: 0.85rem;
  font-weight: 600;
}

.status-badge {
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
}

.status-passed {
  background: #d4edda;
  color: #155724;
}

.status-refer {
  background: #fff3cd;
  color: #856404;
}

.status-failed {
  background: #f8d7da;
  color: #721c24;
}

.status-unknown {
  background: #e9ecef;
  color: #6c757d;
}

.active-badge {
  background: #e6f9f0;
  color: #1a7f5a;
  padding: 0.25rem 0.6rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  border: 1px solid #b2e8d0;
}

.expired-badge {
  background: #dc3545;
  color: #fff;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
}

/* Card Body */
.id-card-body {
  padding: 1rem;
}

.id-field {
  margin-bottom: 0.75rem;
}

.id-field label {
  display: block;
  font-size: 0.75rem;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 0.25rem;
}

.id-field .id-value {
  font-size: 0.95rem;
  color: #212529;
}

/* Match Indicators */
.match-indicator {
  font-size: 0.75rem;
  padding: 0.15rem 0.5rem;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
}

.match-indicator.matched {
  background: #d4edda;
  color: #155724;
}

.match-indicator.not-matched {
  background: #f8d7da;
  color: #721c24;
}

/* MRZ Section */
.mrz-section label {
  font-size: 0.75rem;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.mrz-content {
  background: #1a1a2e;
  border-radius: 6px;
  padding: 0.75rem 1rem;
  overflow-x: auto;
}

.mrz-content pre {
  color: #00ff88;
  font-family: "Courier New", Courier, monospace;
  font-size: 0.85rem;
  letter-spacing: 1px;
  white-space: pre-wrap;
  word-break: break-all;
}

/* Attempts Section */
.attempts-section label {
  font-size: 0.75rem;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.attempts-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.attempt-item {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  padding: 0.75rem;
  border-left: 4px solid #6c757d;
}

.attempt-item.attempt-passed {
  border-left-color: #28a745;
  background: #f8fff9;
}

.attempt-item.attempt-refer {
  border-left-color: #ffc107;
  background: #fffdf5;
}

.attempt-item.attempt-failed {
  border-left-color: #dc3545;
  background: #fff8f8;
}

.attempt-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.attempt-number {
  font-weight: 600;
  color: #495057;
}

.attempt-status {
  font-size: 0.7rem;
  padding: 0.15rem 0.5rem;
  border-radius: 4px;
  text-transform: uppercase;
  font-weight: 600;
}

.attempt-status.attempt-passed {
  background: #d4edda;
  color: #155724;
}

.attempt-status.attempt-refer {
  background: #fff3cd;
  color: #856404;
}

.attempt-status.attempt-failed {
  background: #f8d7da;
  color: #721c24;
}

.attempt-details {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
}

.attempt-detail {
  font-size: 0.8rem;
  color: #6c757d;
}

.attempt-detail i {
  color: #adb5bd;
}

/* Audit Section */
.audit-section {
  padding-top: 0.75rem;
  border-top: 1px dashed #dee2e6;
}

/* ID Image Section */
.id-image-section label {
  font-size: 0.75rem;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.id-image-wrapper {
  position: relative;
  display: inline-block;
  border-radius: 8px;
  overflow: hidden;
  border: 2px solid #e0e0e0;
  cursor: pointer;
  max-width: 300px;
  transition: border-color 0.2s ease;
}

.id-image-wrapper:hover {
  border-color: #5cc9a7;
}

.id-image-wrapper:hover .id-image-overlay {
  opacity: 1;
}

.id-image {
  display: block;
  max-width: 100%;
  height: auto;
  max-height: 200px;
  object-fit: contain;
}

.id-image-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s ease;
  color: #fff;
}

.id-image-overlay i {
  font-size: 1.5rem;
  margin-bottom: 0.5rem;
}

.id-image-overlay span {
  font-size: 0.85rem;
}

.image-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  background: #f8f9fa;
  color: #6c757d;
  min-height: 120px;
}

.image-error i {
  font-size: 2rem;
  margin-bottom: 0.5rem;
}

/* Image Modal */
.image-modal-content {
  background: #1a1a2e;
}

.modal-id-image {
  display: block;
  max-width: 100%;
  max-height: 70vh;
  margin: 0 auto;
  object-fit: contain;
}

.image-modal-info {
  background: #fff;
  padding: 1rem;
  border-top: 1px solid #e0e0e0;
}

.image-modal-info small {
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-size: 0.7rem;
}

.image-modal-info p {
  font-weight: 600;
  color: #212529;
}

/* Utilities */
.text-monospace {
  font-family: "Courier New", Courier, monospace;
}

.text-truncate {
  max-width: 100%;
}
</style>
